import http from '@/utils/http';
import { CanvasElement, ElementStyle, Whiteboard, WhiteboardComment } from './types';

// ---- whiteboards ----

export const listWhiteboards = () => http.get<Whiteboard[]>('/whiteboards').then((r) => r.data);
export const getWhiteboard = (id: string) => http.get<Whiteboard>(`/whiteboards/${id}`).then((r) => r.data);
export const createWhiteboard = (body: Partial<Whiteboard>) =>
  http.post<Whiteboard>('/whiteboards', body).then((r) => r.data);
export const updateWhiteboard = (id: string, body: Partial<Whiteboard>) =>
  http.put<Whiteboard>(`/whiteboards/${id}`, body).then((r) => r.data);
export const deleteWhiteboard = (id: string) => http.delete(`/whiteboards/${id}`).then(() => undefined);

// ---- elements ----

export const listElements = (whiteboardId: string) =>
  http.get<CanvasElement[]>(`/whiteboards/${whiteboardId}/elements`).then((r) => r.data);

/** Create an element. Pass a client-generated `id` so the local optimistic copy already matches the
 *  server row (the API honours a provided id) — no swap needed once the POST returns. */
export const createElement = (whiteboardId: string, body: Partial<CanvasElement>) =>
  http.post<CanvasElement>(`/whiteboards/${whiteboardId}/elements`, body).then((r) => r.data);

/** Generic non-geometry update (content, colour, style, z-order, connector endpoints). */
export const updateElement = (id: string, body: Partial<CanvasElement>) =>
  http.put<CanvasElement>(`/whiteboard-elements/${id}`, body).then((r) => r.data);

/** Narrow geometry write (drag / resize / rotate) — never clobbers a concurrent content edit. */
export const setElementGeometry = (
  id: string,
  g: { x: number; y: number; w: number; h: number; rotation: number },
) => http.put<CanvasElement>(`/whiteboard-elements/${id}/geometry`, g).then((r) => r.data);

/** Batch geometry write for a multi-select drag (one transaction server-side). */
export const batchSetGeometry = (
  whiteboardId: string,
  moves: { id: string; x: number; y: number; w: number; h: number; rotation: number }[],
) => http.put<CanvasElement[]>(`/whiteboards/${whiteboardId}/elements/positions`, { moves }).then((r) => r.data);

/** Soft-delete one element (+ any connectors attached to it). Returns all removed ids (for undo). */
export const deleteElement = (id: string) =>
  http.delete<{ ids: string[] }>(`/whiteboard-elements/${id}`).then((r) => r.data.ids);

/** Soft-delete several elements at once (+ their connectors). */
export const deleteElements = (whiteboardId: string, ids: string[]) =>
  http.post<{ ids: string[] }>(`/whiteboards/${whiteboardId}/elements/delete`, { ids }).then((r) => r.data.ids);

/** Reverse a soft-delete (undo of a delete / redo of a create). */
export const restoreElements = (whiteboardId: string, ids: string[]) =>
  http.post<{ restored: number }>(`/whiteboards/${whiteboardId}/elements/restore`, { ids }).then((r) => r.data.restored);

/** Upload an image and place it on the canvas at (x,y) with natural size (w,h). Returns the new element. */
export const uploadImage = (
  whiteboardId: string,
  file: File,
  geo: { x: number; y: number; w: number; h: number },
) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('x', String(geo.x));
  fd.append('y', String(geo.y));
  fd.append('w', String(geo.w));
  fd.append('h', String(geo.h));
  return http.post<CanvasElement>(`/whiteboards/${whiteboardId}/images`, fd).then((r) => r.data);
};

/** Fetch an image element's bytes with auth (the stream endpoint 401s a bare <img>). */
export const fetchElementImageBlob = (id: string) =>
  http.get(`/whiteboard-elements/${id}/image`, { responseType: 'blob' }).then((r) => r.data as Blob);

/** Fetch a whiteboard's thumbnail bytes with auth (the stream endpoint 401s a bare <img>). The image
 *  is an SVG rendered server-side; the backend renders one on first read if none exists yet. */
export const fetchSnapshotBlob = (whiteboardId: string) =>
  http.get(`/whiteboards/${whiteboardId}/snapshot`, { responseType: 'blob' }).then((r) => r.data as Blob);

// ---- comments (Miro-like pinned threads) ----

/** All live comments on a whiteboard (roots + replies, flat, oldest-first). */
export const listComments = (whiteboardId: string) =>
  http.get<WhiteboardComment[]>(`/whiteboards/${whiteboardId}/comments`).then((r) => r.data);

/** Create a thread root (parent_id omitted, anchored at x/y) or a reply (parent_id set). Pass a
 *  client-generated `id` so the optimistic pin matches the server row. */
export const createComment = (
  whiteboardId: string,
  body: { id?: string; parent_id?: string | null; author_id?: string | null; x: number; y: number; body: string },
) => http.post<WhiteboardComment>(`/whiteboards/${whiteboardId}/comments`, body).then((r) => r.data);

/** Edit a comment's body. */
export const updateComment = (id: string, body: string) =>
  http.put<WhiteboardComment>(`/whiteboard-comments/${id}`, { body }).then((r) => r.data);

/** Toggle a thread's resolved flag. */
export const resolveComment = (id: string, resolved: boolean) =>
  http.put<WhiteboardComment>(`/whiteboard-comments/${id}/resolve`, { resolved }).then((r) => r.data);

/** Reposition a thread pin (narrow world-anchor write — never clobbers a body edit). */
export const moveComment = (id: string, x: number, y: number) =>
  http.put<WhiteboardComment>(`/whiteboard-comments/${id}/position`, { x, y }).then((r) => r.data);

/** Soft-delete a comment (a root cascade-deletes its replies). Returns all removed ids. */
export const deleteComment = (id: string) =>
  http.delete<{ ids: string[] }>(`/whiteboard-comments/${id}`).then((r) => r.data.ids);

/** Reverse a soft-delete (undo). */
export const restoreComments = (whiteboardId: string, ids: string[]) =>
  http.post<{ restored: number }>(`/whiteboards/${whiteboardId}/comments/restore`, { ids }).then((r) => r.data.restored);

/** Toggle the acting member's emoji reaction on a comment. Returns the fresh comment (with reactions). */
export const reactComment = (id: string, emoji: string) =>
  http.put<WhiteboardComment>(`/whiteboard-comments/${id}/react`, { emoji }).then((r) => r.data);

/** The acting member's per-thread seen markers (thread_id → ISO time), for unread badges. */
export const listCommentsSeen = (whiteboardId: string) =>
  http.get<{ seen: Record<string, string> }>(`/whiteboards/${whiteboardId}/comments/seen`).then((r) => r.data.seen ?? {});

/** Mark a thread (its root id) seen for the acting member (clears its unread badge). */
export const markThreadSeen = (threadId: string) =>
  http.put(`/whiteboard-comments/${threadId}/seen`).then(() => undefined);

/** Coerce a server element's `style` to a typed object (defensive against a null/string blob). */
export const styleOf = (el: CanvasElement): ElementStyle => {
  const s = el.style as unknown;
  if (!s) return {};
  if (typeof s === 'string') {
    try {
      return JSON.parse(s) as ElementStyle;
    } catch {
      return {};
    }
  }
  return s as ElementStyle;
};
